import React, { useEffect, useMemo, useState } from 'react';
import api from './api';
import TaskModal from './TaskModal';
import { userDisplayName } from './helpers';

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

function cleJour(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Calendrier consolidé : toutes les échéances, tous projets confondus.
 * C'est la vue qui manquait pour repérer les semaines de surcharge.
 */
function GlobalCalendar({ users, currentUser, onOpenProject }) {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [curseur, setCurseur] = useState(() => new Date());
  const [filtrePersonne, setFiltrePersonne] = useState('tous');
  const [masquerTerminees, setMasquerTerminees] = useState(true);
  const [tacheOuverte, setTacheOuverte] = useState(undefined);

  const charger = () => {
    setChargement(true);
    Promise.all([api.get('/tasks'), api.get('/projects')])
      .then(([t, p]) => {
        // /projects ne renvoie pas les projets archivés : on écarte donc leurs
        // tâches du calendrier pour rester cohérent.
        const visibles = new Set(p.data.map((x) => x.id));
        setTasks(t.data.filter((x) => visibles.has(x.project_id)));
        setProjects(p.data);
      })
      .catch(() => {})
      .finally(() => setChargement(false));
  };

  useEffect(charger, []);

  const aujourdhui = cleJour(new Date());

  const filtrees = useMemo(() => {
    let liste = tasks.filter((t) => t.due_date);
    if (masquerTerminees) liste = liste.filter((t) => t.status !== 'Done');
    if (filtrePersonne !== 'tous') {
      const id = parseInt(filtrePersonne);
      liste = liste.filter((t) => t.assigned_to === id || t.delegated_to === id);
    }
    return liste;
  }, [tasks, filtrePersonne, masquerTerminees]);

  const parJour = useMemo(() => {
    const m = {};
    for (const t of filtrees) {
      const k = String(t.due_date).slice(0, 10);
      if (!m[k]) m[k] = [];
      m[k].push(t);
    }
    return m;
  }, [filtrees]);

  // Grille du mois, commençant un lundi.
  const grille = useMemo(() => {
    const annee = curseur.getFullYear();
    const mois = curseur.getMonth();
    const premier = new Date(annee, mois, 1);
    const decalage = (premier.getDay() + 6) % 7; // lundi = 0
    const debut = new Date(annee, mois, 1 - decalage);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(debut);
      d.setDate(debut.getDate() + i);
      return d;
    });
  }, [curseur]);

  const nomProjet = (id) => projects.find((p) => p.id === id)?.name || '';
  const changerMois = (delta) =>
    setCurseur(new Date(curseur.getFullYear(), curseur.getMonth() + delta, 1));

  if (chargement) return <p>Chargement...</p>;

  return (
    <div className="fade-in">
      <div className="page-header-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="btn btn-sm" onClick={() => changerMois(-1)}>←</button>
          <div style={{ fontSize: 16, fontWeight: 600, minWidth: 160, textAlign: 'center' }}>
            {MOIS[curseur.getMonth()]} {curseur.getFullYear()}
          </div>
          <button className="btn btn-sm" onClick={() => changerMois(1)}>→</button>
          <button className="btn btn-sm" onClick={() => setCurseur(new Date())}>Aujourd'hui</button>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <label style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={masquerTerminees}
              onChange={(e) => setMasquerTerminees(e.target.checked)}
            />
            Masquer les tâches terminées
          </label>
          <select
            className="btn btn-sm"
            value={filtrePersonne}
            onChange={(e) => setFiltrePersonne(e.target.value)}
          >
            <option value="tous">Toute l'équipe</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{userDisplayName(u)}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10 }}>
        {filtrees.length} échéance{filtrees.length > 1 ? 's' : ''} affichée{filtrees.length > 1 ? 's' : ''}
        {filtrePersonne !== 'tous' && ' pour cette personne'}
      </div>

      <div className="cal-grid-head">
        {JOURS.map((j) => <div key={j} className="cal-head-cell">{j}</div>)}
      </div>

      <div className="cal-grid">
        {grille.map((d) => {
          const k = cleJour(d);
          const hors = d.getMonth() !== curseur.getMonth();
          const taches = parJour[k] || [];
          const estAujourdhui = k === aujourdhui;

          return (
            <div key={k} className={`cal-cell ${hors ? 'hors-mois' : ''} ${estAujourdhui ? 'aujourdhui' : ''}`}>
              <div className="cal-cell-date">{d.getDate()}</div>
              {taches.slice(0, 4).map((t) => {
                const enRetard = k < aujourdhui && t.status !== 'Done';
                return (
                  <div
                    key={t.id}
                    className={`cal-task ${enRetard ? 'en-retard' : ''} ${t.est_jalon ? 'calendar-jalon-chip' : ''}`}
                    title={`${t.est_jalon ? 'Jalon : ' : ''}${t.name} — ${nomProjet(t.project_id)} — ${userDisplayName(users.find((u) => u.id === t.assigned_to))}`}
                    onClick={() => (t.est_jalon ? onOpenProject(projects.find((p) => p.id === t.project_id), 'jalons') : setTacheOuverte(t))}
                  >
                    {t.est_jalon ? '◆ ' : ''}{t.name}
                  </div>
                );
              })}
              {taches.length > 4 && (
                <div className="cal-plus">+{taches.length - 4} autres</div>
              )}
            </div>
          );
        })}
      </div>

      {tacheOuverte !== undefined && (
        <TaskModal
          task={tacheOuverte}
          projectId={tacheOuverte?.project_id}
          users={users}
          currentUser={currentUser}
          onClose={() => setTacheOuverte(undefined)}
          onSaved={() => { setTacheOuverte(undefined); charger(); }}
          onDeleted={() => { setTacheOuverte(undefined); charger(); }}
        />
      )}
    </div>
  );
}

export default GlobalCalendar;
